<template>
  <b-dropdown
    variant="bg-white"
    class="select-project"
    no-flip
    @hidden="currentPage = projectPage"
  >
    <template #button-content>
      <GjIcon
        class="icon-left bd-highlight"
        name="Widget"
      />
      <span class="text text-left flex-grow-1 bd-highlight">
        <template v-if="!loading && navbarProjects">
          {{ projectName }}
        </template>
        <template v-else>
          <div class="loading">
            <GjIcon name="Loader" />
          </div>
        </template>
      </span>
      <GjIcon
        class="icon-right bd-highlight"
        name="ArrowDown"
      />
    </template>
    <div
      v-if="totalCount > 10"
      class="default-pagination pt-1 justify-content-center"
    >
      <button
        role="menuitemradio"
        type="button"
        class="page-link extra"
        :disabled="!resultInfo.hasPreviousPage"
        @click="currentPage--"
      >
        &lsaquo;
      </button>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalCount"
        :per-page="pageSize"
        prev-class="d-none"
        next-class="d-none"
        hide-goto-end-buttons
      />
      <button
        role="menuitemradio"
        type="button"
        class="page-link extra"
        :disabled="!resultInfo.hasNextPage"
        @click="currentPage++"
      >
        &rsaquo;
      </button>
    </div>
    <div
      v-if="totalCount > pageSize || filter"
      class="d-flex justify-content-center search-bar"
    >
      <b-input-group>
        <b-input-group-prepend is-text>
          <b-icon-search />
        </b-input-group-prepend>
        <b-form-input
          id="filterInput"
          v-model.trim="filter"
          type="search"
          class="m-0"
          placeholder="Type to Search"
          @keyup.enter="fetchProjects"
        />
      </b-input-group>
    </div>
    <div class="position-relative">
      <template v-for="proj in navbarProjects">
        <b-dropdown-item
          :key="proj.id"
          role="test"
          :class="{ active: proj.id == projectId }"
          @click="changeProject(proj)"
        >
          {{ proj.name }}
        </b-dropdown-item>
      </template>
      <table-busy
        v-if="fetchingProjects"
        style="top: 0; height: 100%;"
      />
    </div>
    <b-dropdown-divider v-if="totalCount > 10" />
    <b-dropdown-item
      class="link"
      @click="$router.push({ name: 'project-add' })"
    >
      + New Project
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
import { BIconSearch } from 'bootstrap-vue'

export default {
  components: { BIconSearch },
  data() {
    return {
      projects: [],
      currentPage: 1,
      loading: true,
      filter: '',
      typingTimer: null,
      doneTypingInterval: 500,
    }
  },
  computed: {
    projectName() {
      return this.$store.state.app.projectName
    },
    projectId() {
      return this.$store.state.app.projectId
    },
    fetchingProjects() {
      return this.$store.state.projects.fetchingProjects
    },
    navbarProjects() {
      return this.$store.state.projects.projects
    },
    resultInfo() {
      return this.$store.state.projects.resultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return 5
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    lastPage() {
      return this.currentPage === Math.floor(this.totalCount / this.pageSize)
    },
    projectPage() {
      return this.$store.state.app.projectPage + 1
    },
  },
  watch: {
    currentPage() {
      this.fetchProjects()
    },
    filter() {
      clearTimeout(this.typingTimer)
      this.typingTimer = setTimeout(() => {
        this.onChangeSearch()
      }, this.doneTypingInterval)
    },
  },
  created() {
    this.$store
      .dispatch('projects/getProject', this.projectId)
      .then(res => {
        this.$store.commit(
          'app/SET_PROJECT_PAGE',
          Math.floor((res.data.result.orderId - 1) / 5),
        )
        this.$store.commit(
          'app/SET_ORGANIZATION_ID',
          res.data.result.organizationId,
        )
        this.currentPage = this.projectPage
        this.fetchProjects()
      })
      .catch(() => {})
  },
  methods: {
    changeProject(proj) {
      this.$store.commit('app/SET_PROJECT_ID', proj.id)
      this.$store.commit('app/SET_PROJECT_NAME', proj.name)
      this.$store.commit('app/SET_PROJECT_PAGE', this.pageIndex)
      if (this.$route.meta.redirectTo) {
        this.$router.push({
          name: this.$route.meta.redirectTo,
          params: {
            projectId: proj.id,
            updateState: true,
          },
        })
      } else {
        this.$router.push({
          name: this.$route.name,
          params: {
            projectId: proj.id,
            updateState: true,
          },
        })
      }
      this.$store.commit('RESET_STATE')
      this.$store.commit('app/UPDATE_KEY')
    },
    fetchProjects() {
      this.$store
        .dispatch('projects/fetchProjects', {
          pageIndex: this.pageIndex,
          pageSize: this.pageSize,
          search: this.filter,
        })
        .then(() => {
          const selectedProj = this.navbarProjects.find(
            p => p.id === this.projectId,
          )
          if (selectedProj) {
            this.$store.commit('app/SET_PROJECT_NAME', selectedProj.name)
            this.$store.commit('app/SET_PROJECT_ID', selectedProj.id)
          }
        })
        .catch(error => {
          this.errors = error.response.data.errors
        })
        .finally(() => {
          this.loading = false
        })
    },
    onChangeSearch() {
      if (this.currentPage === 1) {
        this.fetchProjects()
      } else {
        this.currentPage = 1
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.select-project {
  padding: 0 30px;
  width: 100%;
  ::v-deep {
    .dropdown-toggle {
      &::after {
        content: none;
      }
      display: flex;
      width: 100%;
      overflow: hidden;

      .bd-highlight {
        max-width: calc(100% - 68px);
        text-overflow: ellipsis;
        overflow: hidden;
      }
    }

    .dropdown-menu {
      li.active {
        a {
          background-color: var(--primary);
          color: var(--white);
        }
      }
    }

    .dropdown-menu {
      width: calc(100% - 60px);
    }

    .dropdown-divider {
      margin: 0;
    }
    .page-link {
      &.extra {
        background-color: transparent;
        border: none;
        box-shadow: none;
        &:disabled {
          color: #b9b9c3;
        }
      }
    }
    .search-bar {
      div, input {
        border-radius: 0 !important;
      }
    }
    .link {
      .dropdown-item {
        font-weight: 700;
        text-align: center;
        color: var(--primary) !important;
      }
    }
  }
}
</style>
